import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { LucideDynamicIcon, LucideFunnel, LucideX } from '@lucide/angular';
import { InputComponent } from '../../atoms/input/input.component';
import { DropdownComponent, DropdownOption } from '../../molecules/dropdown/dropdown.component';
import { MultiSelectComponent } from '../../molecules/multi-select/multi-select.component';
import {
  SelectPortalContainerId,
  SelectPortalContainerService,
} from '../../utils/select-portal-container/select-portal-container.service';
import type { TableColumnFilter } from './table.types';

/**
 * Funnel button for a `joii-table` header that opens a popover holding the column's filter control.
 * Text input is debounced; selects emit immediately.
 */
@Component({
  selector: 'joii-table-column-filter',
  imports: [
    CdkOverlayOrigin,
    CdkConnectedOverlay,
    LucideDynamicIcon,
    InputComponent,
    DropdownComponent,
    MultiSelectComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <button
      type="button"
      class="joii-table-filter-button"
      cdkOverlayOrigin
      #origin="cdkOverlayOrigin"
      [class.joii-table-filter-button--active]="isActive()"
      [attr.aria-label]="'Filter ' + label()"
      [attr.aria-expanded]="open()"
      (click)="toggle($event)"
    >
      <svg [lucideIcon]="funnelIcon" [size]="16" />
    </button>
    <ng-template
      cdkConnectedOverlay
      [cdkConnectedOverlayOrigin]="origin"
      [cdkConnectedOverlayOpen]="open()"
      [cdkConnectedOverlayPositions]="positions"
      [cdkConnectedOverlayHasBackdrop]="true"
      cdkConnectedOverlayBackdropClass="cdk-overlay-transparent-backdrop"
      (backdropClick)="open.set(false)"
      (attach)="registerPortalContainer()"
      (detach)="onDetach()"
    >
      <div class="joii-table-filter-panel" (click)="$event.stopPropagation()">
        @switch (config().type) {
          @case ('input') {
            <joii-input
              [value]="textValue()"
              [placeholder]="config().placeholder ?? 'Search...'"
              [ariaLabel]="'Filter ' + label()"
              (valueChange)="onText($any($event))"
            />
          }
          @case ('single-select') {
            <joii-dropdown
              [options]="options()"
              [value]="selectedOption()"
              [placeholder]="config().placeholder ?? 'Select'"
              (valueChange)="onSingle($event)"
            />
          }
          @case ('multi-select') {
            <joii-multi-select
              [options]="options()"
              [value]="multiValue()"
              [placeholder]="config().placeholder ?? 'Select'"
              (valueChange)="emit($event)"
            />
          }
        }
        <button type="button" class="joii-table-filter-clear" aria-label="Clear filter" (click)="clear()">
          <svg [lucideIcon]="clearIcon" [size]="16" />
        </button>
      </div>
    </ng-template>
  `,
  styles: `
    :host {
      display: inline-flex;
    }
    .joii-table-filter-button,
    .joii-table-filter-clear {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 0;
      border: none;
      background: transparent;
      cursor: pointer;
      color: var(--Vetai-Texts--Secondary);
    }
    .joii-table-filter-button:hover,
    .joii-table-filter-clear:hover,
    .joii-table-filter-button--active {
      color: var(--Vetai-Inline_links_and_info--Default);
    }
    .joii-table-filter-panel {
      display: flex;
      align-items: center;
      gap: var(--Vetai-Spacing-2);
      padding: var(--Vetai-Spacing-2) var(--Vetai-Spacing-3);
      border-radius: var(--Vetai-Radius-2);
      background: var(--Vetai-Backgrounds--Base);
      box-shadow: var(--Vetai-Elevation-Small);
    }
  `,
})
export class TableColumnFilterComponent {
  readonly config = input.required<TableColumnFilter>();
  /** Current filter value. */
  readonly value = input<unknown>(null);
  /** Column label for aria text. */
  readonly label = input<string>('');
  readonly valueChange = output<unknown>();

  readonly open = signal(false);
  readonly funnelIcon = LucideFunnel;
  readonly clearIcon = LucideX;
  readonly positions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 8 },
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 8 },
  ];

  private readonly overlay = viewChild.required(CdkConnectedOverlay);
  private readonly selectPortalContainer = inject(SelectPortalContainerService);
  private portalContainerId: SelectPortalContainerId | null = null;
  private debounceHandle: ReturnType<typeof setTimeout> | undefined;

  constructor() {
    inject(DestroyRef).onDestroy(() => {
      clearTimeout(this.debounceHandle);
      this.unregisterPortalContainer();
    });
  }

  readonly options = computed<DropdownOption[]>(() => {
    const config = this.config();
    return config.type === 'input' ? [] : config.options;
  });
  readonly textValue = computed(() => (this.value() as string | null) ?? '');
  readonly multiValue = computed(() => (Array.isArray(this.value()) ? (this.value() as unknown[]) : []));
  readonly selectedOption = computed(() => this.options().find((option) => option.value === this.value()));
  readonly isActive = computed(() => {
    const value = this.value();
    return Array.isArray(value) ? value.length > 0 : value !== null && value !== undefined && value !== '';
  });

  /**
   * The overlay renders in the top layer, above anything in `document.body`, so dropdown and
   * multi-select panels must render inside the filter panel to stay clickable.
   */
  protected registerPortalContainer(): void {
    this.unregisterPortalContainer();
    const { overlayElement } = this.overlay().overlayRef;
    const panel = overlayElement.querySelector<HTMLElement>('.joii-table-filter-panel') ?? overlayElement;
    this.portalContainerId = this.selectPortalContainer.register(panel);
  }

  protected onDetach(): void {
    this.unregisterPortalContainer();
    this.open.set(false);
  }

  toggle(event: MouseEvent): void {
    event.stopPropagation();
    this.open.update((open) => !open);
  }

  onText(value: string | null): void {
    clearTimeout(this.debounceHandle);
    const config = this.config();
    const delay = config.type === 'input' ? (config.debounceMs ?? 500) : 0;
    this.debounceHandle = setTimeout(() => this.emit(value), delay);
  }

  onSingle(option: DropdownOption | undefined): void {
    this.emit(option?.value ?? null);
  }

  emit(value: unknown): void {
    this.valueChange.emit(value);
  }

  clear(): void {
    clearTimeout(this.debounceHandle);
    this.emit(this.config().type === 'multi-select' ? [] : null);
  }

  private unregisterPortalContainer(): void {
    if (this.portalContainerId) {
      this.selectPortalContainer.unregister(this.portalContainerId);
      this.portalContainerId = null;
    }
  }
}
